import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from 'lodash';
import * as actions from "../../../store/actions";
import { i18n } from '../../../translate/i18n';
import {
  Paper, Container, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, TextField, Typography, CircularProgress,
} from '@mui/material';
import Spinner from '../../../components/Spinner/index';
import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TableActionButtons from "../../../UI/MaterialTableOverrides/TableActionButtons";
import TableActionConfirmButtons from "../../../UI/MaterialTableOverrides/TableActionConfirmButtons";
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';
import { deleteCheckMessage } from "../../../utils/deleteCheckMessage";


export default function Areas() {
  const dispatch = useDispatch();
  let textInput = useRef(null);

  const [page, setPage] = useState(1);
  const [alreadySerched, setAlreadySerched] = useState(false);
  const [limit, setLimit] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [firstLoad, setFirstLoad] = useState(true);

  const [editedIndex, setEditedIndex] = useState(-1);
  const [areaEditText, setAreaEditText] = useState("");
  const [isAddingNewArea, setIsAddingNewArea] = useState(false);
  const [actionRow, setActionRow] = useState("");

  const fetchAreas = useCallback(
    () => dispatch(actions.fetchAreas(page, limit, searchText, order, orderBy)),
    [page, limit, searchText, order, orderBy]
  );

	useEffect(() => {
		dispatch(actions.setOpenDrawer(true))
	}, [])

  const fetchCheckDelete = useCallback(
    (index) => dispatch(actions.checkDeleteAreas(index)));

  const deleteArea = (index, page, limit, searchText, order, orderBy) => dispatch(actions.deleteAreas(index, page, limit, searchText, order, orderBy))
  const updateArea = (index, body, page, limit, searchText, order, orderBy) => dispatch(actions.updateAreas(index, body, page, limit, searchText, order, orderBy))
  const postAreas = (body, page, limit, searchText, order, orderBy) => dispatch(actions.postAreas(body, page, limit, searchText, order, orderBy))

  const areas = useSelector(state => state.areas.areas);
  const areasFilter = useSelector(state => state.areas.areasFilter);
  const checkDeleteAreas = useSelector(state => state.areas.checkDelete);

  const loading = useSelector(state => state.areas.loading);

  const headRows = [
    { id: "actions", numeric: false, disablePadding: false, label: i18n.t('headRowsAreas.actions') },
    { id: "name", numeric: false, disablePadding: false, label: i18n.t('headRowsAreas.area') }
  ];

  useEffect(() => {
    if (!isEmpty(areasFilter)) {
      setPage(areasFilter.page);
      setLimit(areasFilter.limit);
      setSearchText(areasFilter.searchText);
      setOrder(areasFilter.order);
      setOrderBy(areasFilter.orderBy);
    }
    setFirstLoad(false);
  }, []);

  useEffect(() => {
    if (!firstLoad) {
      fetchAreas(page, limit, searchText, order, orderBy);
      setAlreadySerched(true);
    }
  }, [firstLoad, page, limit, order, orderBy]);

  useEffect(() => {
    if (!firstLoad) {
      if (searchText !== "" || alreadySerched) {
        const timer = setTimeout(() => {
          if (searchText === textInput.current.value) {
            fetchAreas(page, limit, searchText, order, orderBy);
          }
        }, 200);
        return () => {
          clearTimeout(timer);
        };
      }
    }
  }, [searchText, textInput]);

  const handleChangePage = (e, page) => {
    setEditedIndex(-1);
    setPage(page + 1);
    setIsAddingNewArea(false)
  };

  const handleChangeRowsPerPage = e => {
    setLimit(e.target.value);
    setPage(1);
    setEditedIndex(-1);
    setIsAddingNewArea(false)
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const editComponent = (index, obj) => {
    setEditedIndex(index);
    setAreaEditText(obj.name);
    setActionRow('editing');
    setIsAddingNewArea(false)
  };

  const cancelEdit = () => {
    setEditedIndex(-1);
  };

  const deleteComponent = (index, objIndex) => {
    fetchCheckDelete(objIndex);
    setEditedIndex(index);
    setActionRow('deleting');
  };

  const updateComponent = (index) => {
    if (actionRow === 'editing') {
      if (areaEditText === '') return;
      updateArea(index, { name: areaEditText }).then((res) => {
        if (res === 'success') {
          setEditedIndex(-1);
          fetchAreas();
        }
      })
    } else if (actionRow === 'deleting') {
      deleteArea(index).then((res) => {
        if (res === 'success') {
          setEditedIndex(-1);
          fetchAreas();
        }
      })
    }
  };

  const addNewAreaRow = () => {
    setIsAddingNewArea(!isAddingNewArea)
    setEditedIndex(-1);
    setAreaEditText("");
  }

  const createComponent = () => {
    postAreas({ name: areaEditText }).then((res) => {
      if (res === 'success') {
        setIsAddingNewArea(false)
        fetchAreas();
      }
    })
  }

  const createCancel = () => {
    setIsAddingNewArea(false)
  }

  const editArea = () => {
    return (
      <TextField
        variant="standard"
        style={styles.editWidth}
        multiline
        id="area"
        value={areaEditText}
        margin="normal"
        onChange={event => {
          setAreaEditText(event.target.value.toUpperCase());
        }} />
    );
  };

  const styles = {
    table: {
      position: 'relative',
      marginBottom: 50,
    },
    opacityContainer: {
      opacity: loading ? '0.3' : '1',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    deleteTextConfirm: {
      marginLeft: '-30px',
      fontWeight: 'bold',
    },
    editWidth: {
      width: '90%'
    }
  }

  return <>
    <Container maxWidth="sm">
      <Paper>
        <TableTitle
          title={i18n.t('tableTitleAreas.title')}
          placeholder={i18n.t('tableTitleAreas.placeholder')}
          inputRef={textInput}
          value={searchText}
          onChange={event => {
            setPage(1);
            setSearchText(event.target.value);
          }}
          addRow={() => addNewAreaRow()}
        />
        <Table style={styles.table}>
          <tbody>
            <tr>
              <td>
                {loading &&
                  <div style={styles.spinner}>
                    <Spinner />
                  </div>}
                {areas.rows.length === 0 && !isAddingNewArea &&
                  <div style={styles.noResults}>
                    {!loading && <Typography style={styles.spinner}>{i18n.t('fetchAreas.text')}</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            style={styles.opacityContainer}
            headRows={headRows}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody style={styles.opacityContainer}>
            {isAddingNewArea &&
              <TableRow>
                <TableActionConfirmButtons
                  createComponent={createComponent}
                  createCancel={createCancel}
                />
                <TableCell>{editArea()}</TableCell>
              </TableRow>
            }
            {areas.rows.map((item, index) => (
              <TableRow key={item._id}>
                <TableActionButtons
                  objContent={item}
                  deleteComponent={deleteComponent}
                  editComponent={editComponent}
                  updateComponent={updateComponent}
                  cancelEdit={cancelEdit}
                  editedIndex={editedIndex}
                  index={index}
                  action={actionRow}
                  checkDelete={isEmpty(checkDeleteAreas)}
                />
                <TableCell>
                  {index === editedIndex ?
                    actionRow === 'editing' ?
                      editArea() :
                      <Typography variant='h6' style={styles.deleteTextConfirm}>
                        {isEmpty(checkDeleteAreas) ? i18n.t('tableCellAreas.condition') : deleteCheckMessage(checkDeleteAreas)}
                      </Typography> :
                    item.name
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter style={styles.opacityContainer}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={areas.count}
                rowsPerPage={parseInt(limit)}
                page={page - 1}
                SelectProps={{
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActionsWrapped}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </Container>
  </>;

}



